import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Checkbox,
    FormControlLabel,
    IconButton,
    Tooltip,
    CircularProgress,
    Alert,
    Divider,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    LinearProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
    Download as DownloadIcon,
    PictureAsPdf as PdfIcon,
    TableChart as ExcelIcon,
    Visibility as ViewIcon,
    CalendarToday as CalendarIcon,
    Assignment as AssignmentIcon,
    Person as PersonIcon,
    Business as BusinessIcon,
    CheckCircle as CheckCircleIcon,
    Pending as PendingIcon,
    PlayArrow as InProgressIcon,
    Close as CloseIcon,
    Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import AssignmentService from '../../../Services/assignment.service';
import UserService from '../../../Services/user.service';
import Constants from '../../../assests/Constants/constants';
import UserAvatar from '../../Widgets/UserAvatar/UserAvatar.js';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import CustomDatePicker from '../../Widgets/CustomDates/CustomDatePicker/CustomDatePicker';
import CustomMonthDatePicker from '../../Widgets/CustomDates/CustomMonthDatePicker/CustomMonthDatePicker';
import AutocompleteSelect from '../../Widgets/AutocompleteSelect/AutocompleteSelect';

// Styled Components
const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: 12,
        minHeight: '80vh',
        direction: 'inherit',
        '& *': {
            boxSizing: 'border-box !important'
        }
    }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    backgroundColor: '#304FFF',
    color: 'white',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    textAlign: "left",
    position: "relative",
    fontWeight: "bold",
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
    padding: theme.spacing(3),
    marginTop: "20px",
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
    justifyContent: "center",
    gap: "20px",
    padding: "16px",
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledCloseIconButton = styled(IconButton)(({ theme }) => ({
    color: 'white',
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledHeaderBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledRootContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    textAlign: "left",
    marginTop: "10px",
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledSectionCard = styled(Card)(({ theme }) => ({
    elevation: 2,
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledSectionCardContent = styled(CardContent)(({ theme }) => ({
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledSectionTitle = styled(Typography)(({ theme }) => ({
    textAlign: "left",
    margin: "0 0 2px 0",
    fontWeight: "bold",
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledFiltersContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledFiltersRow = styled(Box)(({ theme }) => ({
    display: "flex",
    gap: 2,
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledFilterBox = styled(Box)(({ theme }) => ({
    flex: 1,
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledFilterLabel = styled(Typography)(({ theme }) => ({
    display: "flex",
    justifyContent: "flex-end",
    gap: 1,
    flexDirection: "column",
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledFilterContainer = styled(Box)(({ theme }) => ({
    direction: "ltr",
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledTable = styled(Table)(({ theme }) => ({
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    textAlign: 'center',
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledTableHeadCell = styled(TableCell)(({ theme }) => ({
    textAlign: 'center',
    backgroundColor: '#f5f5f5',
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:hover': {
        backgroundColor: '#f0f8ff'
    },
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledCheckboxCell = styled(TableCell)(({ theme }) => ({
    padding: "checkbox",
    textAlign: 'center',
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledSummaryCard = styled(Card)(({ theme }) => ({
    elevation: 3,
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledSummaryCardContent = styled(CardContent)(({ theme }) => ({
    textAlign: "center",
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledSummaryNumber = styled(Typography)(({ theme }) => ({
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledSummaryLabel = styled(Typography)(({ theme }) => ({
    color: "textSecondary",
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledButton = styled(Button)(({ theme }) => ({
    fontWeight: "bold",
    padding: "10px 20px",
    fontSize: "14px",
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledCancelButton = styled(StyledButton)(({ theme }) => ({
    border: "2px solid #757575",
    color: "#424242",
    "&:hover": {
        backgroundColor: "#f5f5f5",
    },
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledGenerateButton = styled(StyledButton)(({ theme }) => ({
    background: "linear-gradient(45deg, #007AFF 30%, #0051D2 90%)",
    color: "#fff",
    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
    "&:hover": {
        background: "linear-gradient(45deg, #0051D2 30%, #003D91 90%)",
    },
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledPdfButton = styled(StyledButton)(({ theme }) => ({
    border: "2px solid #d32f2f",
    color: "#d32f2f",
    "&:hover": {
        backgroundColor: "#ffebee",
        borderColor: "#d32f2f",
    },
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledExcelButton = styled(StyledButton)(({ theme }) => ({
    border: "2px solid #2e7d32",
    color: "#2e7d32",
    "&:hover": {
        backgroundColor: "#e8f5e8",
        borderColor: "#2e7d32",
    },
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledBackButton = styled(StyledButton)(({ theme }) => ({
    border: "2px solid #757575",
    color: "#424242",
    "&:hover": {
        backgroundColor: "#f5f5f5",
    },
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledSelectionHeader = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledSelectionNote = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    fontStyle: 'italic',
    color: "textSecondary",
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledStatusBox = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: 1,
    justifyContent: "center",
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledProgressBox = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: 2,
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledProgressBar = styled(Box)(({ theme }) => ({
    flex: 1,
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const WorkPlanManager = ({ open, onClose, assignments, subAccountants, caseIdOptions, categoryOptions }) => {
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();
    const [loading, setLoading] = useState(false);
    const [selectedAssignments, setSelectedAssignments] = useState([]);
    const [workPlanRequest, setWorkPlanRequest] = useState({
        startDate: dayjs().startOf('week').toDate(),
        endDate: dayjs().endOf('week').toDate(),
        assignmentIds: [],
        caseId: null,
        assigneeId: null,
        status: null
    });
    const [reportType, setReportType] = useState('custom');
    const [generatedReport, setGeneratedReport] = useState(null);
    const [showReport, setShowReport] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [user, setUser] = useState(null);
    const [token, setToken] = useState('');
    
    useEffect(() => {
        const fetchUserData = async () => {
            const currentUser = await UserService.getCurrentUser();
            const currentToken = await UserService.getCurrentToken();
            setUser(currentUser);
            setToken(currentToken);
        };
        fetchUserData();
    }, []);

    const handleAssignmentSelection = (assignmentId, checked) => {
        if (checked) {
            setSelectedAssignments(prev => [...prev, assignmentId]);
        } else {
            setSelectedAssignments(prev => prev.filter(id => id !== assignmentId));
        }
    };

    const handleSelectAllAssignments = (checked) => {
        if (checked) {
            setSelectedAssignments(filteredAssignments.map(a => a.id));
        } else {
            setSelectedAssignments([]);
        }
    };

    const filteredAssignments = useMemo(() => {
        let filtered = assignments;

        // Only apply filters if they are explicitly set (not showing "All" options)
        if (workPlanRequest.caseId && workPlanRequest.caseId !== 'all') {
            filtered = filtered.filter(a => a.caseId === workPlanRequest.caseId);
        }

        if (workPlanRequest.assigneeId && workPlanRequest.assigneeId !== 'all') {
            filtered = filtered.filter(a => a.assigneeId === workPlanRequest.assigneeId);
        }

        if (workPlanRequest.status !== null && workPlanRequest.status !== 'all') {
            filtered = filtered.filter(a => a.status === workPlanRequest.status);
        }

        return filtered;
    }, [assignments, workPlanRequest]);

    const handleGenerateReport = async () => {
        if (!user?.accountantId) {
            toast.error(t('UserNotAuthenticated'));
            return;
        }

        // Validate that either assignments are selected or a period is specified
        if (selectedAssignments.length === 0 && (!workPlanRequest.startDate || !workPlanRequest.endDate)) {
            toast.error(t('PleaseSelectAssignmentsOrPeriod'));
            return;
        }

        setLoading(true);
        try {
            const request = {
                accountantId: user.accountantId,
                startDate: workPlanRequest.startDate,
                endDate: workPlanRequest.endDate,
                assignmentIds: selectedAssignments.length > 0 ? selectedAssignments : [],
                // Only send filters if no specific assignments are selected
                caseId: selectedAssignments.length === 0 ? (workPlanRequest.caseId || null) : null,
                assigneeId: selectedAssignments.length === 0 ? (workPlanRequest.assigneeId || null) : null,
                status: selectedAssignments.length === 0 ? workPlanRequest.status : null
            };

            const response = await AssignmentService.GenerateWorkPlanReport(request, token);
            if (response.ok) {
                const report = await response.json();
                setGeneratedReport(report);
                setShowReport(true);
                toast.success(t('ReportGeneratedSuccessfully'));
            } else {
                toast.error(t('FailedToGenerateReport'));
            }
        } catch (error) {
            console.error('Error generating report:', error);
            toast.error(t('ErrorGeneratingReport'));
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPdf = async () => {
        if (!user?.accountantId) return;

        setLoading(true);
        try {
            const request = {
                accountantId: user.accountantId,
                startDate: workPlanRequest.startDate,
                endDate: workPlanRequest.endDate,
                assignmentIds: selectedAssignments.length > 0 ? selectedAssignments : [],
                // Only send filters if no specific assignments are selected
                caseId: selectedAssignments.length === 0 ? (workPlanRequest.caseId || null) : null,
                assigneeId: selectedAssignments.length === 0 ? (workPlanRequest.assigneeId || null) : null,
                status: selectedAssignments.length === 0 ? workPlanRequest.status : null
            };

            await AssignmentService.GenerateWorkPlanPdf(request, token);
            toast.success(t('PdfDownloadedSuccessfully'));
        } catch (error) {
            console.error('Error downloading PDF:', error);
            toast.error(t('ErrorDownloadingPdf'));
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadExcel = async () => {
        if (!user?.accountantId) return;

        setLoading(true);
        try {
            const request = {
                accountantId: user.accountantId,
                startDate: workPlanRequest.startDate,
                endDate: workPlanRequest.endDate,
                assignmentIds: selectedAssignments.length > 0 ? selectedAssignments : [],
                // Only send filters if no specific assignments are selected
                caseId: selectedAssignments.length === 0 ? (workPlanRequest.caseId || null) : null,
                assigneeId: selectedAssignments.length === 0 ? (workPlanRequest.assigneeId || null) : null,
                status: selectedAssignments.length === 0 ? workPlanRequest.status : null
            };

            await AssignmentService.GenerateWorkPlanExcel(request, token);
            toast.success(t('ExcelDownloadedSuccessfully'));
        } catch (error) {
            console.error('Error downloading Excel:', error);
            toast.error(t('ErrorDownloadingExcel'));
        } finally {
            setLoading(false);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case Constants.StatusEnum.Done:
                return <CheckCircleIcon color="success" />;
            case Constants.StatusEnum.InProgress:
                return <InProgressIcon color="primary" />;
            case Constants.StatusEnum.ToDo:
                return <PendingIcon color="warning" />;
            default:
                return <PendingIcon color="disabled" />;
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case Constants.PriorityEnum.Critical:
                return 'error';
            case Constants.PriorityEnum.High:
                return 'warning';
            case Constants.PriorityEnum.Medium:
                return 'info';
            case Constants.PriorityEnum.Low:
                return 'success';
            default:
                return 'default';
        }
    };

    const getPriorityEmoji = (priority) => {
        switch (priority) {
            case Constants.PriorityEnum.Critical:
                return Constants.PriorityEmojis.Critical;
            case Constants.PriorityEnum.High:
                return Constants.PriorityEmojis.High;
            case Constants.PriorityEnum.Low:
                return Constants.PriorityEmojis.Low;
            default:
                return 'default';
        }
    };

    const getAssigneeName = (assigneeId) => {
        const assignee = subAccountants.find(sa => sa.subAccountantId === assigneeId);
        return assignee ? assignee.name : t('NoAssignee');
    };

    const getClientName = (caseId) => {
        const client = caseIdOptions.find(c => c.value === caseId);
        return client ? client.label : '-';
    };

    return (
        <StyledDialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            disableScrollLock
            hideBackdrop
            disableAutoFocus
            disableEnforceFocus
            disableRestoreFocus
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    minHeight: '80vh',
                    direction: direction
                }
            }}
        >
            <StyledDialogTitle>
                <StyledCloseIconButton onClick={onClose}>
                    <CloseIcon />
                </StyledCloseIconButton>
                <StyledHeaderBox>
                    <AssignmentIcon />
                    <Typography variant="h6">{t('WorkPlanManager')}</Typography>
                </StyledHeaderBox>
            </StyledDialogTitle>

            <StyledDialogContent dir={direction}>
                {!showReport ? (
                    <StyledRootContainer>
                        {/* Report Configuration */}
                        <StyledSectionCard>
                            <StyledSectionCardContent>
                                <StyledSectionTitle>
                                    {t('ReportConfiguration')}
                                </StyledSectionTitle>

                                <StyledFiltersContainer>
                                    {/* Date Range */}
                                    <StyledFiltersRow>




                                        <StyledFilterBox>
                                            <StyledFilterLabel>
                                                <Box component="strong">{t('StartDate')}</Box>
                                                <Box sx={{ direction: "ltr" }}>
                                                    <CustomDatePicker
                                                        date={workPlanRequest.startDate ? dayjs(workPlanRequest.startDate) : null}
                                                        onDateChange={(date) => {
                                                            if (date) {
                                                                setWorkPlanRequest(prev => ({
                                                                    ...prev,
                                                                    startDate: date
                                                                }));
                                                            }
                                                        }}
                                                        height="48px"
                                                    />
                                                </Box>
                                            </StyledFilterLabel>
                                        </StyledFilterBox>

                                        <StyledFilterBox>
                                            <StyledFilterLabel>
                                                <Box component="strong">{t('EndDate')}</Box>
                                                <Box sx={{ direction: "ltr" }}>
                                                    <CustomDatePicker
                                                        date={workPlanRequest.endDate ? dayjs(workPlanRequest.endDate) : null}
                                                        onDateChange={(date) => {
                                                            if (date) {
                                                                setWorkPlanRequest(prev => ({
                                                                    ...prev,
                                                                    endDate: date
                                                                }));
                                                            }
                                                        }}
                                                        height="48px"
                                                    />
                                                </Box>
                                            </StyledFilterLabel>
                                        </StyledFilterBox>
                                    </StyledFiltersRow>

                                    {/* Filters */}
                                    <StyledFiltersRow>
                                        <StyledFilterBox>
                                            <StyledFilterLabel>
                                                <Box component="strong">{t('Client')}</Box>
                                                <StyledFilterContainer>
                                                    <AutocompleteSelect
                                                        options={[
                                                            { value: null, label: t('AllClients') },
                                                            ...caseIdOptions.map(option => ({
                                                                value: option.value,
                                                                label: option.label
                                                            }))
                                                        ]}
                                                        selectedValues={
                                                            workPlanRequest.caseId
                                                                ? (() => {
                                                                    const foundClient = caseIdOptions.find(c => c.value === workPlanRequest.caseId);
                                                                    return foundClient 
                                                                        ? { value: foundClient.value, label: foundClient.label }
                                                                        : { value: null, label: t('AllClients') };
                                                                  })()
                                                                : { value: null, label: t('AllClients') }
                                                        }
                                                        onChange={(event, values) => {
                                                            setWorkPlanRequest(prev => ({
                                                                ...prev,
                                                                caseId: values?.value || null
                                                            }));
                                                        }}
                                                        placeholder={t('SelectClient')}
                                                        isMultiple={false}
                                                        width="300px"
                                                        isDisabled={false}
                                                    />
                                                </StyledFilterContainer>
                                            </StyledFilterLabel>
                                        </StyledFilterBox>

                                        <StyledFilterBox>
                                            <StyledFilterLabel>
                                                <Box component="strong">{t('Assignee')}</Box>
                                                <StyledFilterContainer>
                                                    <AutocompleteSelect
                                                        options={[
                                                            { value: null, label: t('AllAssignees') },
                                                            ...subAccountants.map(assignee => ({
                                                                value: assignee.subAccountantId,
                                                                label: assignee.name
                                                            }))
                                                        ]}
                                                        selectedValues={
                                                            workPlanRequest.assigneeId
                                                                ? (() => {
                                                                    const foundAssignee = subAccountants.find(sa => sa.subAccountantId === workPlanRequest.assigneeId);
                                                                    return foundAssignee 
                                                                        ? { value: foundAssignee.subAccountantId, label: foundAssignee.name }
                                                                        : { value: null, label: t('AllAssignees') };
                                                                  })()
                                                                : { value: null, label: t('AllAssignees') }
                                                        }
                                                        onChange={(event, values) => {
                                                            setWorkPlanRequest(prev => ({
                                                                ...prev,
                                                                assigneeId: values?.value || null
                                                            }));
                                                        }}
                                                        placeholder={t('SelectAssignee')}
                                                        isMultiple={false}
                                                        width="300px"
                                                        isDisabled={false}
                                                    />
                                                </StyledFilterContainer>
                                            </StyledFilterLabel>
                                        </StyledFilterBox>
                                    </StyledFiltersRow>

                                    <StyledFiltersRow>
                                        <StyledFilterBox>
                                            <StyledFilterLabel>
                                                <Box component="strong">{t('Status')}</Box>
                                                <StyledFilterContainer>
                                                    <AutocompleteSelect
                                                        options={[
                                                            { value: null, label: t('AllStatuses') },
                                                            { value: Constants.StatusEnum.ToDo, label: t('ToDo') },
                                                            { value: Constants.StatusEnum.InProgress, label: t('InProgress') },
                                                            { value: Constants.StatusEnum.Done, label: t('Done') }
                                                        ]}
                                                        selectedValues={
                                                            workPlanRequest.status !== null
                                                                ? { value: workPlanRequest.status, label: t(workPlanRequest.status === Constants.StatusEnum.ToDo ? 'ToDo' : workPlanRequest.status === Constants.StatusEnum.InProgress ? 'InProgress' : 'Done') }
                                                                : { value: null, label: t('AllStatuses') }
                                                        }
                                                        onChange={(event, values) => {
                                                            setWorkPlanRequest(prev => ({
                                                                ...prev,
                                                                status: values?.value
                                                            }));
                                                        }}
                                                        placeholder={t('SelectStatus')}
                                                        isMultiple={false}
                                                        width="300px"
                                                        isDisabled={false}
                                                    />
                                                </StyledFilterContainer>
                                            </StyledFilterLabel>
                                        </StyledFilterBox>
                                    </StyledFiltersRow>
                                </StyledFiltersContainer>
                            </StyledSectionCardContent>
                        </StyledSectionCard>

                        {/* Assignment Selection */}
                        <StyledSectionCard>
                            <StyledSectionCardContent>
                                <StyledSelectionHeader>
                                    <Typography variant="h6" color="primary" sx={{ textAlign: "left", margin: "0 0 2px 0", fontWeight: "bold" }}>
                                        {t('AssignmentSelection')} : {selectedAssignments.length > 0 && `(${selectedAssignments.length} ${t('Selected')})`}
                                    </Typography>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={selectedAssignments.length === filteredAssignments.length && filteredAssignments.length > 0}
                                                    indeterminate={selectedAssignments.length > 0 && selectedAssignments.length < filteredAssignments.length}
                                                    onChange={(e) => handleSelectAllAssignments(e.target.checked)}
                                                />
                                            }
                                            label={t('SelectAll')}
                                        />
                                    </Box>
                                </StyledSelectionHeader>
                                <StyledSelectionNote>
                                    {t('WorkPlanPeriodNote')}
                                </StyledSelectionNote>
                                <StyledTableContainer component={Paper} elevation={1}>
                                    <StyledTable>
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                                <StyledCheckboxCell padding="checkbox">
                                                    <Checkbox />
                                                </StyledCheckboxCell>
                                                <StyledTableCell>{t('Summary')}</StyledTableCell>
                                                <StyledTableCell>{t('Assignee')}</StyledTableCell>
                                                <StyledTableCell>{t('Client')}</StyledTableCell>
                                                <StyledTableCell>{t('Priority')}</StyledTableCell>
                                                <StyledTableCell>{t('Status')}</StyledTableCell>
                                                <StyledTableCell>{t('DueDate')}</StyledTableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {filteredAssignments
                                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                .map((assignment) => (
                                                    <StyledTableRow key={assignment.id} hover>
                                                        <StyledCheckboxCell padding="checkbox">
                                                            <Checkbox
                                                                checked={selectedAssignments.includes(assignment.id)}
                                                                onChange={(e) => handleAssignmentSelection(assignment.id, e.target.checked)}
                                                            />
                                                        </StyledCheckboxCell>
                                                        <StyledTableCell>
                                                            <Typography variant="body2" noWrap>
                                                                {assignment.summary}
                                                            </Typography>
                                                        </StyledTableCell>
                                                        <StyledTableCell>
                                                            <UserAvatar name={getAssigneeName(assignment.assigneeId)} justifyContent="center" />
                                                        </StyledTableCell>
                                                        <StyledTableCell>
                                                            <UserAvatar name={getClientName(assignment.caseId)} justifyContent="center" />
                                                        </StyledTableCell>
                                                        <StyledTableCell>
                                                            {/* <Chip
                                                                label={getPriorityEmoji(assignment.priority)}
                                                                color={getPriorityColor(assignment.priority)}
                                                                size="small"
                                                            /> */}
                                                            {getPriorityEmoji(assignment.priority)}
                                                        </StyledTableCell>
                                                        <StyledTableCell>
                                                            <StyledStatusBox>
                                                                {getStatusIcon(assignment.status)}
                                                                <Typography variant="body2">
                                                                    {t(assignment.status)}
                                                                </Typography>
                                                            </StyledStatusBox>
                                                        </StyledTableCell>
                                                        <StyledTableCell>
                                                            {assignment.dueDate ? dayjs(assignment.dueDate).format('MMM DD, YYYY') : '-'}
                                                        </StyledTableCell>
                                                    </StyledTableRow>
                                                ))}
                                        </TableBody>
                                    </StyledTable>
                                </StyledTableContainer>

                                <TablePagination
                                    component="div"
                                    count={filteredAssignments.length}
                                    page={page}
                                    onPageChange={(e, newPage) => setPage(newPage)}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={(e) => {
                                        setRowsPerPage(parseInt(e.target.value, 10));
                                        setPage(0);
                                    }}
                                    labelRowsPerPage={t('RowsPerPage')}
                                    labelDisplayedRows={({ from, to, count }) =>
                                        `${from}-${to} ${t('of')} ${count}`
                                    }
                                />
                            </StyledSectionCardContent>
                        </StyledSectionCard>
                    </StyledRootContainer>
                ) : (
                    /* Report Display */
                    <Box>
                        {generatedReport && (
                            <Grid container spacing={3}>
                                {/* Summary Cards */}
                                <Grid item xs={12}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={3}>
                                            <StyledSummaryCard sx={{ backgroundColor: '#e3f2fd' }}>
                                                <StyledSummaryCardContent>
                                                    <StyledSummaryNumber variant="h4" color="primary">
                                                        {generatedReport.summary.totalAssignments}
                                                    </StyledSummaryNumber>
                                                    <StyledSummaryLabel variant="body2" color="textSecondary">
                                                        {t('TotalAssignments')}
                                                    </StyledSummaryLabel>
                                                </StyledSummaryCardContent>
                                            </StyledSummaryCard>
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <StyledSummaryCard sx={{ backgroundColor: '#e8f5e8' }}>
                                                <StyledSummaryCardContent>
                                                    <StyledSummaryNumber variant="h4" color="success.main">
                                                        {generatedReport.summary.completedAssignments}
                                                    </StyledSummaryNumber>
                                                    <StyledSummaryLabel variant="body2" color="textSecondary">
                                                        {t('Completed')}
                                                    </StyledSummaryLabel>
                                                </StyledSummaryCardContent>
                                            </StyledSummaryCard>
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <StyledSummaryCard sx={{ backgroundColor: '#fff3e0' }}>
                                                <StyledSummaryCardContent>
                                                    <StyledSummaryNumber variant="h4" color="warning.main">
                                                        {generatedReport.summary.inProgressAssignments}
                                                    </StyledSummaryNumber>
                                                    <StyledSummaryLabel variant="body2" color="textSecondary">
                                                        {t('InProgress')}
                                                    </StyledSummaryLabel>
                                                </StyledSummaryCardContent>
                                            </StyledSummaryCard>
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <StyledSummaryCard sx={{ backgroundColor: '#fce4ec' }}>
                                                <StyledSummaryCardContent>
                                                    <StyledSummaryNumber variant="h4" color="error.main">
                                                        {generatedReport.summary.pendingAssignments}
                                                    </StyledSummaryNumber>
                                                    <StyledSummaryLabel variant="body2" color="textSecondary">
                                                        {t('Pending')}
                                                    </StyledSummaryLabel>
                                                </StyledSummaryCardContent>
                                            </StyledSummaryCard>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                {/* Completion Rate */}
                                <Grid item xs={12}>
                                    <StyledSectionCard>
                                        <StyledSectionCardContent>
                                            <Typography variant="h6" gutterBottom color="primary">
                                                {t('CompletionRate')}
                                            </Typography>
                                            <StyledProgressBox>
                                                <StyledProgressBar>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={generatedReport.summary.completionRate * 100}
                                                        sx={{ height: 20, borderRadius: 10 }}
                                                    />
                                                </StyledProgressBar>
                                                <Typography variant="h6" color="primary">
                                                    {(generatedReport.summary.completionRate * 100).toFixed(1)}%
                                                </Typography>
                                            </StyledProgressBox>
                                        </StyledSectionCardContent>
                                    </StyledSectionCard>
                                </Grid>

                                {/* Assignments List */}
                                <Grid item xs={12}>
                                    <StyledSectionCard>
                                        <StyledSectionCardContent>
                                            <Typography variant="h6" gutterBottom color="primary">
                                                {t('Assignments')}
                                            </Typography>
                                            <StyledTableContainer>
                                                <StyledTable>
                                                    <TableHead>
                                                        <TableRow>
                                                            <StyledTableCell>{t('Summary')}</StyledTableCell>
                                                            <StyledTableCell>{t('Assignee')}</StyledTableCell>
                                                            <StyledTableCell>{t('Client')}</StyledTableCell>
                                                            <StyledTableCell>{t('Priority')}</StyledTableCell>
                                                            <StyledTableCell>{t('Status')}</StyledTableCell>
                                                            <StyledTableCell>{t('DueDate')}</StyledTableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {generatedReport.assignments && generatedReport.assignments.length > 0 ? (
                                                            generatedReport.assignments.map((assignment) => (
                                                                <TableRow key={assignment.id}>
                                                                    <StyledTableCell>{assignment.summary}</StyledTableCell>
                                                                    <StyledTableCell>
                                                                        <UserAvatar name={assignment.assigneeName || 'לא מוקצה'} justifyContent="center" />
                                                                    </StyledTableCell>
                                                                    <StyledTableCell>
                                                                        <UserAvatar name={assignment.clientName || 'משרד'} justifyContent="center" />
                                                                    </StyledTableCell>
                                                                    <StyledTableCell>
                                                                        <Chip
                                                                            label={getPriorityEmoji(assignment.priority) + ' ' + t(assignment.priority)}
                                                                            color={getPriorityColor(assignment.priority)}
                                                                            size="small"
                                                                        />
                                                                    </StyledTableCell>
                                                                    <StyledTableCell sx={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                        <StyledStatusBox>
                                                                            {getStatusIcon(assignment.status)}
                                                                            <Typography variant="body2">
                                                                                {t(assignment.status)}
                                                                            </Typography>
                                                                        </StyledStatusBox>
                                                                    </StyledTableCell>
                                                                    <StyledTableCell>
                                                                        {assignment.dueDate ? dayjs(assignment.dueDate).format('DD/MM/YYYY') : '-'}
                                                                    </StyledTableCell>
                                                                </TableRow>
                                                            ))
                                                        ) : (
                                                            <TableRow>
                                                                <StyledTableCell colSpan={6} sx={{ textAlign: 'center' }}>
                                                                    {t('NoAssignmentsFound')}
                                                                </StyledTableCell>
                                                            </TableRow>
                                                        )}
                                                    </TableBody>
                                                </StyledTable>
                                            </StyledTableContainer>
                                        </StyledSectionCardContent>
                                    </StyledSectionCard>
                                </Grid>

                                {/* Assignee Summaries */}
                                {generatedReport.summary?.assigneeSummaries && generatedReport.summary.assigneeSummaries.length > 0 && (
                                    <Grid item xs={12}>
                                        <StyledSectionCard>
                                            <StyledSectionCardContent>
                                                <Typography variant="h6" gutterBottom color="primary">
                                                    {t('AssigneeSummaries')}
                                                </Typography>
                                                <StyledTableContainer>
                                                    <StyledTable>
                                                        <TableHead>
                                                            <TableRow>
                                                                <StyledTableCell>{t('Assignee')}</StyledTableCell>
                                                                <StyledTableCell>{t('TotalAssigned')}</StyledTableCell>
                                                                <StyledTableCell>{t('Completed')}</StyledTableCell>
                                                                <StyledTableCell>{t('InProgress')}</StyledTableCell>
                                                                <StyledTableCell>{t('Pending')}</StyledTableCell>
                                                                <StyledTableCell>{t('CompletionRate')}</StyledTableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {generatedReport.summary.assigneeSummaries.map((assignee, index) => (
                                                                <TableRow key={index}>
                                                                    <StyledTableCell>
                                                                        <UserAvatar name={assignee.assigneeName} justifyContent="center" />
                                                                    </StyledTableCell>
                                                                    <StyledTableCell sx={{ textAlign: 'center' }}>{assignee.totalAssigned}</StyledTableCell>
                                                                    <StyledTableCell sx={{ textAlign: 'center', color: 'success.main' }}>{assignee.completed}</StyledTableCell>
                                                                    <StyledTableCell sx={{ textAlign: 'center', color: 'warning.main' }}>{assignee.inProgress}</StyledTableCell>
                                                                    <StyledTableCell sx={{ textAlign: 'center', color: 'error.main' }}>{assignee.pending}</StyledTableCell>
                                                                    <StyledTableCell sx={{ textAlign: 'center' }}>
                                                                        <Chip
                                                                            label={`${(assignee.completionRate * 100).toFixed(1)}%`}
                                                                            color={assignee.completionRate >= 0.8 ? 'success' : assignee.completionRate >= 0.5 ? 'warning' : 'error'}
                                                                            size="small"
                                                                        />
                                                                    </StyledTableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </StyledTable>
                                                </StyledTableContainer>
                                            </StyledSectionCardContent>
                                        </StyledSectionCard>
                                    </Grid>
                                )}
                            </Grid>
                        )}
                    </Box>
                )}
            </StyledDialogContent>

            <StyledDialogActions className={`${direction}`}>
                {!showReport ? (
                    <>
                        <StyledCancelButton
                            variant="outlined"
                            color="inherit"
                            startIcon={<CloseIcon />}
                            onClick={onClose}
                        >
                            {t('Cancel')}
                        </StyledCancelButton>
                        <StyledGenerateButton
                            onClick={handleGenerateReport}
                            variant="contained"
                            startIcon={<ViewIcon />}
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={20} /> : t('GenerateReport')}
                        </StyledGenerateButton>
                    </>
                ) : (
                    <>
                        <StyledBackButton
                            onClick={() => setShowReport(false)}
                            color="inherit"
                        >
                            {t('Back')}
                        </StyledBackButton>
                        <StyledPdfButton
                            onClick={handleDownloadPdf}
                            variant="outlined"
                            startIcon={<PdfIcon />}
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={20} /> : t('DownloadPdf')}
                        </StyledPdfButton>
                        <StyledExcelButton
                            onClick={handleDownloadExcel}
                            variant="outlined"
                            startIcon={<ExcelIcon />}
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={20} /> : t('DownloadExcel')}
                        </StyledExcelButton>
                    </>
                )}
            </StyledDialogActions>
        </StyledDialog>
    );
};

export default WorkPlanManager; 